import { Icon, LssProp, TextInput } from '@lucidhq/lucidium';
import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { useLocation } from 'react-router-dom';

import { SearchParamsValuesType, useSetUrlParams } from '@/hooks';

export interface RemoteTextSearchProps {
  search: string;
  lss?: LssProp;
  placeholder?: string;
  paramValue?: string;
  setCompanyName?: (companyName?: string) => void;
  setFilterParams?: (param: SearchParamsValuesType) => void;
  minChars?: number;
}

export const RemoteTextSearch = ({
  search,
  lss,
  setCompanyName,
  placeholder = 'Search',
  paramValue = '',
  setFilterParams,
  minChars = 0,
}: RemoteTextSearchProps) => {
  const [params, setParams] = useSetUrlParams();
  const [inputValue, setInputValue] = useState(params[search] || paramValue);

  const location = useLocation();

  const handleChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
      setInputValue(value),
    []
  );

  const updateParams = useCallback(() => {
    if (inputValue.length >= minChars || !inputValue) {
      const paramsToUpdate = { [search]: inputValue || undefined };

      setFilterParams?.(paramsToUpdate);
      !setFilterParams && setParams?.(paramsToUpdate, { replace: true });
      setCompanyName && setCompanyName(location?.state?.companyName);
    }
  }, [
    inputValue,
    minChars,
    search,
    setFilterParams,
    setParams,
    setCompanyName,
    location?.state?.companyName,
  ]);

  const handleClick = useCallback(() => {
    updateParams();
  }, [updateParams]);

  const handleKeyDown = useCallback(
    ({ key }: KeyboardEvent<HTMLInputElement>) => {
      if (key === 'Enter') {
        updateParams();
      }
    },
    [updateParams]
  );

  return (
    // @ts-ignore
    <TextInput
      name="remote-search-text"
      value={inputValue}
      lss={{ height: '42px', ...lss }}
      placeholder={placeholder}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      rightIcon={
        <Icon
          data-testid="search-button"
          as={MdSearch}
          disabled={!inputValue || inputValue.length < minChars}
          size={24}
          lss={{
            cursor: 'pointer',
            ':not([disabled]):active': {
              transform: 'scale(0.8)',
            },
          }}
          onClick={handleClick}
        />
      }
    />
  );
};
